import {get} from "lodash"
import BaseService from "./Base"
import {
    CoreCampusActivityMapping,
    CoreCampusClient,
    CoreCampusIntegrationRun,
    CoreCampusPaginatedResponse,
    CoreCampusProfileSync,
    CoreCampusRefreshReferenceDataResponse,
    CoreCampusStaffMapping,
    CoreCampusStatusMapping,
    CoreCampusUserMapping,
    DeleteCoreCampusActivityMappingPayload,
    DeleteCoreCampusStaffMappingPayload,
    DeleteCoreCampusStatusMappingPayload,
    ListCoreCampusActivityMappingsPayload,
    ListCoreCampusClientsPayload,
    ListCoreCampusProfileSyncsPayload,
    ListCoreCampusRunsPayload,
    ListCoreCampusStaffMappingsPayload,
    ListCoreCampusStatusMappingsPayload,
    ListCoreCampusUserMappingsPayload,
    RefreshCoreCampusReferenceDataPayload,
    RunCoreCampusMatchingPayload,
    UpdateCoreCampusUserMappingPayload,
    UpsertCoreCampusActivityMappingPayload,
    UpsertCoreCampusStaffMappingPayload,
    UpsertCoreCampusStatusMappingPayload
} from "types/integrations/coreCampus"

class CoreCampusIntegrationService extends BaseService {
    async listClients(params: ListCoreCampusClientsPayload): Promise<CoreCampusPaginatedResponse<CoreCampusClient>> {
        const response = await this.post("/v1/integrations/core-campus/clients/list", params)
        return get(response, "data", {success: false, total: 0, data: []})
    }

    async listRuns(params: ListCoreCampusRunsPayload): Promise<CoreCampusPaginatedResponse<CoreCampusIntegrationRun>> {
        const response = await this.post("/v1/integrations/core-campus/runs/list", params)
        return get(response, "data", {success: false, total: 0, data: []})
    }

    async listProfileSyncs(
        params: ListCoreCampusProfileSyncsPayload
    ): Promise<CoreCampusPaginatedResponse<CoreCampusProfileSync>> {
        const response = await this.post("/v1/integrations/core-campus/profile-syncs/list", params)
        return get(response, "data", {success: false, total: 0, data: []})
    }

    async listUserMappings(
        params: ListCoreCampusUserMappingsPayload
    ): Promise<CoreCampusPaginatedResponse<CoreCampusUserMapping>> {
        const response = await this.post("/v1/integrations/core-campus/user-mappings/list", params)
        return get(response, "data", {success: false, total: 0, data: []})
    }

    async updateUserMapping(
        params: UpdateCoreCampusUserMappingPayload
    ): Promise<{success: boolean; data: {id: number; studentProfileId: number | null}}> {
        const response = await this.post("/v1/integrations/core-campus/user-mappings/update", params)
        return get(response, "data", {
            success: false,
            data: {id: params.mappingId, studentProfileId: params.studentProfileId}
        })
    }

    async listStatusMappings(
        params: ListCoreCampusStatusMappingsPayload
    ): Promise<CoreCampusPaginatedResponse<CoreCampusStatusMapping>> {
        const response = await this.post("/v1/integrations/core-campus/status-mappings/list", params)
        return get(response, "data", {success: false, total: 0, data: []})
    }

    async upsertStatusMapping(
        params: UpsertCoreCampusStatusMappingPayload
    ): Promise<{success: boolean; data: CoreCampusStatusMapping}> {
        const response = await this.post("/v1/integrations/core-campus/status-mappings/upsert", params)
        return get(response, "data", {success: false, data: null})
    }

    async deleteStatusMapping(
        params: DeleteCoreCampusStatusMappingPayload
    ): Promise<{success: boolean; data: {id: number}}> {
        const response = await this.post("/v1/integrations/core-campus/status-mappings/delete", params)
        return get(response, "data", {success: false, data: {id: params.id}})
    }

    async listActivityMappings(
        params: ListCoreCampusActivityMappingsPayload
    ): Promise<CoreCampusPaginatedResponse<CoreCampusActivityMapping>> {
        const response = await this.post("/v1/integrations/core-campus/activity-mappings/list", params)
        return get(response, "data", {success: false, total: 0, data: []})
    }

    async upsertActivityMapping(
        params: UpsertCoreCampusActivityMappingPayload
    ): Promise<{success: boolean; data: CoreCampusActivityMapping}> {
        const response = await this.post("/v1/integrations/core-campus/activity-mappings/upsert", params)
        return get(response, "data", {success: false, data: null})
    }

    async deleteActivityMapping(
        params: DeleteCoreCampusActivityMappingPayload
    ): Promise<{success: boolean; data: {id: number}}> {
        const response = await this.post("/v1/integrations/core-campus/activity-mappings/delete", params)
        return get(response, "data", {success: false, data: {id: params.id}})
    }

    async listStaffMappings(
        params: ListCoreCampusStaffMappingsPayload
    ): Promise<CoreCampusPaginatedResponse<CoreCampusStaffMapping>> {
        const response = await this.post("/v1/integrations/core-campus/staff-mappings/list", params)
        return get(response, "data", {success: false, total: 0, data: []})
    }

    async upsertStaffMapping(
        params: UpsertCoreCampusStaffMappingPayload
    ): Promise<{success: boolean; data: CoreCampusStaffMapping}> {
        const response = await this.post("/v1/integrations/core-campus/staff-mappings/upsert", params)
        return get(response, "data", {success: false, data: null})
    }

    async deleteStaffMapping(
        params: DeleteCoreCampusStaffMappingPayload
    ): Promise<{success: boolean; data: {id: number}}> {
        const response = await this.post("/v1/integrations/core-campus/staff-mappings/delete", params)
        return get(response, "data", {success: false, data: {id: params.id}})
    }

    async refreshReferenceData(
        params: RefreshCoreCampusReferenceDataPayload
    ): Promise<{success: boolean; data: CoreCampusRefreshReferenceDataResponse}> {
        const response = await this.post("/v1/integrations/core-campus/reference-data/refresh", params)
        return get(response, "data", {success: false, data: {progressCodes: [], counselors: []}})
    }

    async runMatching(params: RunCoreCampusMatchingPayload = {}): Promise<{success: boolean; data: any}> {
        const response = await this.post("/v1/integrations/core-campus/matching/run", params)
        return get(response, "data", {success: false, data: null})
    }
}

export default CoreCampusIntegrationService
